import {
  Box,
  Card,
  CardContent,
  Chip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { memo } from "react";
import { IndustrialEquipment } from "../../api-models/IndustrialEquipment";

const useStyles = makeStyles((theme) => ({
  card: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    transition: "transform 0.2s, box-shadow 0.2s",
    cursor: "pointer",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: theme.shadows[8],
    },
  },
  content: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
  },
  header: {
    marginBottom: theme.spacing(1),
  },
  eqhubId: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: "0.875rem",
    marginBottom: theme.spacing(0.5),
  },
  name: {
    fontWeight: 600,
    fontSize: "1.1rem",
    marginBottom: theme.spacing(0.5),
  },
  type: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    marginBottom: theme.spacing(1.5),
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(0.75),
  },
  label: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  value: {
    fontSize: "0.875rem",
    fontWeight: 500,
  },
  statusContainer: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
    flexWrap: "wrap",
  },
  manufacturerChip: {
    fontWeight: 600,
    fontSize: "0.875rem",
  },
  approvedChip: {
    backgroundColor: "#4caf50",
    color: "white",
  },
  publishedChip: {
    backgroundColor: "#2196f3",
    color: "white",
  },
  draftChip: {
    backgroundColor: "#ff9800",
    color: "white",
  },
  price: {
    fontSize: "1.25rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
    marginTop: theme.spacing(1),
  },
}));

interface ProductCardProps {
  equipment: IndustrialEquipment;
  onClick?: () => void;
}

const ProductCardComponent = ({ equipment, onClick }: ProductCardProps) => {
  const classes = useStyles();

  // Formatta lo status per la visualizzazione
  const formatStatus = (status: string) => {
    return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <Card className={classes.card} onClick={onClick}>
      <CardContent className={classes.content}>
        <Box className={classes.header}>
          <Typography className={classes.eqhubId}>
            EQH-{equipment.eqhubProductId}
          </Typography>
          <Typography className={classes.name}>
            {equipment.requirementsClass.name}
          </Typography>
          <Typography className={classes.type}>
            {equipment.type || "Product"}
          </Typography>
        </Box>

        <Box>
          <Box className={classes.infoRow}>
            <Typography className={classes.label}>Manufacturer</Typography>
            <Chip
              label={equipment.manufacturerName}
              size="small"
              className={classes.manufacturerChip}
              color="primary"
            />
          </Box>

          <Box className={classes.infoRow}>
            <Typography className={classes.label}>Part Number</Typography>
            <Typography className={classes.value}>
              {equipment.partNumber || "N/A"}
            </Typography>
          </Box>

          <Box className={classes.infoRow}>
            <Typography className={classes.label}>Model Number</Typography>
            <Typography className={classes.value}>
              {equipment.modelNumber || "N/A"}
            </Typography>
          </Box>

          <Box className={classes.infoRow}>
            <Typography className={classes.label}>Class</Typography>
            <Typography className={classes.value}>
              {equipment.requirementsClass.name}
            </Typography>
          </Box>

          {equipment.verifierCompany && (
            <Box className={classes.infoRow}>
              <Typography className={classes.label}>Verifier</Typography>
              <Typography className={classes.value}>
                {equipment.verifierCompany.name}
              </Typography>
            </Box>
          )}
        </Box>

        <Box className={classes.statusContainer}>
          <Box>
            <Typography className={classes.label} style={{ marginBottom: 4 }}>
              Status
            </Typography>
            <Chip
              label={formatStatus(equipment.status)}
              size="small"
              className={
                equipment.status === "published"
                  ? classes.publishedChip
                  : classes.draftChip
              }
            />
          </Box>
          <Box>
            <Typography className={classes.label} style={{ marginBottom: 4 }}>
              Verification
            </Typography>
            <Chip
              label={formatStatus(equipment.verificationStatus)}
              size="small"
              className={classes.approvedChip}
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// Memoize to prevent unnecessary re-renders
export const ProductCard = memo(
  ProductCardComponent,
  (prevProps, nextProps) => {
    return prevProps.equipment.id === nextProps.equipment.id;
  }
);
